/**
 * PricingModal — in-app plan picker shown as a dialog overlay.
 *
 * Replaces the legacy "redirect to /settings?tab=billing" pattern for
 * upsell CTAs across the app. Keeps the user in context, shows the
 * available plans + cadence toggle, then launches Stripe Checkout
 * inline. (Stripe still owns card capture — we just don't navigate
 * away from the current page to get there.)
 *
 * Usage:
 *   const [open, setOpen] = useState(false);
 *   <PricingModal open={open} onOpenChange={setOpen} reason="storage" />
 */
import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { apiFetch } from '../fetchInterceptor';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Loader2, Check, Sparkles, Building2 } from 'lucide-react';
import { cn } from '../lib/utils';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function PricingModal({
  open,
  onOpenChange,
  reason,           // optional copy hint: 'storage' | 'documents' | 'ai_chat' | 'plaid' | 'trial'
  defaultCadence = 'monthly',
}) {
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [cadence, setCadence] = useState(defaultCadence);
  const [checkingOut, setCheckingOut] = useState(null);

  useEffect(() => {
    if (!open) return;
    let alive = true;
    (async () => {
      setLoading(true);
      try {
        const res = await apiFetch(`${API_URL}/api/billing/plans`);
        if (res.ok) {
          const d = await res.json();
          if (alive) setPlans(d.plans || []);
        }
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [open]);

  const HEADLINES = {
    storage:   { title: 'Need more storage?',                  body: 'Pick a plan that covers your scan volume. Cancel anytime.' },
    documents: { title: 'Unlock document uploads + bank sync', body: 'Every paid plan includes uploads, OCR, and Plaid bank connections.' },
    ai_chat:   { title: 'Keep premium AI flowing',             body: 'Upgrade for a larger Claude / GPT cap. Free Llama questions stay unlimited.' },
    plaid:     { title: 'Connect more bank accounts',          body: 'Each plan tier includes more Plaid connections.' },
    trial:     { title: 'Pick your plan to start your trial',  body: 'Free for 7 days. Cancel before day 8 to avoid being charged.' },
    default:   { title: 'Pick a plan',                          body: 'Upgrade unlocks more AI, storage, and bank connections.' },
  };
  const headline = HEADLINES[reason] || HEADLINES.default;

  const startCheckout = async (planId) => {
    setCheckingOut(planId);
    try {
      const res = await apiFetch(`${API_URL}/api/billing/checkout`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          item_type: 'plan',
          item_id: planId,
          origin_url: window.location.origin,
          cadence,
        }),
      });
      const d = await res.json().catch(() => ({}));
      if (res.ok && d.url) {
        window.location.href = d.url;
        return;
      }
      toast.error(d.detail || 'Could not start checkout.');
    } catch (e) {
      toast.error(e.message || 'Network error');
    }
    setCheckingOut(null);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl" data-testid="pricing-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Sparkles className="w-4 h-4 text-emerald-500" />
            {headline.title}
          </DialogTitle>
          <DialogDescription>{headline.body}</DialogDescription>
        </DialogHeader>

        {/* Cadence toggle */}
        <div className="flex justify-center gap-1 p-1 bg-muted rounded-full w-fit mx-auto">
          <button
            type="button"
            onClick={() => setCadence('monthly')}
            data-testid="pricing-cadence-monthly"
            className={cn(
              'px-3 py-1 text-xs rounded-full transition-colors',
              cadence === 'monthly' ? 'bg-background shadow-sm font-medium' : 'text-muted-foreground'
            )}
          >
            Monthly
          </button>
          <button
            type="button"
            onClick={() => setCadence('annual')}
            data-testid="pricing-cadence-annual"
            className={cn(
              'px-3 py-1 text-xs rounded-full transition-colors',
              cadence === 'annual' ? 'bg-background shadow-sm font-medium' : 'text-muted-foreground'
            )}
          >
            Annual <Badge variant="outline" className="ml-1 text-[9px] h-4 px-1">−15%</Badge>
          </button>
        </div>

        {loading ? (
          <div className="py-16 flex justify-center">
            <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 mt-2">
            {plans.filter((p) => (p.id || p.plan_id) !== 'free').map((p) => {
              const planId = p.id || p.plan_id;
              const basePrice = p.price ?? p.price_usd ?? 0;
              const annualPrice = p.annual_price_usd ?? Math.round(basePrice * 12 * 0.85);
              const price = cadence === 'annual'
                ? Math.round(annualPrice / 12)
                : basePrice;
              const isLoading = checkingOut === planId;
              const isHighlighted = planId === 'pro' || p.is_recommended;
              return (
                <div
                  key={planId}
                  data-testid={`pricing-plan-${planId}`}
                  className={cn(
                    'rounded-xl border bg-card p-4 flex flex-col gap-3 relative transition-shadow',
                    isHighlighted && 'border-emerald-500 shadow-lg ring-1 ring-emerald-500/20'
                  )}
                >
                  {isHighlighted && (
                    <Badge className="absolute -top-2 left-1/2 -translate-x-1/2 bg-emerald-500 text-white text-[10px]">
                      Most popular
                    </Badge>
                  )}
                  <div>
                    <h4 className="font-semibold capitalize flex items-center gap-1.5">
                      <Building2 className="w-3.5 h-3.5 text-muted-foreground" />
                      {p.name || planId}
                    </h4>
                    <div className="mt-1 flex items-baseline gap-1">
                      <span className="text-2xl font-bold tabular-nums">
                        ${price}
                      </span>
                      <span className="text-xs text-muted-foreground">/mo</span>
                    </div>
                    {cadence === 'annual' && (
                      <p className="text-[10px] text-emerald-600 dark:text-emerald-400 mt-0.5">
                        Billed yearly · save 15%
                      </p>
                    )}
                  </div>

                  <ul className="space-y-1 text-xs text-muted-foreground flex-1">
                    {(p.features || []).slice(0, 5).map((f, i) => (
                      <li key={i} className="flex items-start gap-1.5">
                        <Check className="w-3 h-3 text-emerald-500 mt-0.5 shrink-0" />
                        <span>{f}</span>
                      </li>
                    ))}
                  </ul>

                  <Button
                    onClick={() => startCheckout(planId)}
                    disabled={isLoading || checkingOut != null}
                    size="sm"
                    className={cn(
                      'w-full',
                      isHighlighted && 'bg-emerald-600 hover:bg-emerald-500 text-white'
                    )}
                    data-testid={`pricing-plan-${planId}-cta`}
                  >
                    {isLoading ? (
                      <Loader2 className="w-3.5 h-3.5 animate-spin" />
                    ) : (
                      `Start ${p.name || planId} →`
                    )}
                  </Button>
                </div>
              );
            })}
          </div>
        )}

        <p className="text-[11px] text-muted-foreground text-center mt-2">
          7-day free trial · cancel anytime · secure checkout by Stripe
        </p>
      </DialogContent>
    </Dialog>
  );
}
